import React, { useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, TextField, Typography, Alert,
  InputAdornment, IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Logo from "../../Images/logo_main_black.webp";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import { useLoginMutation } from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setEnableAuth } from "../../Store/Auth/AuthSlice";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const BRAND_COLOR = "#0f172a";
const BRAND_GRADIENT = "linear-gradient(160deg, #0f172a 0%, #1e293b 40%, #004d54 100%)";
const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8f9ff" } };

type Mode = "login" | "forgot" | "forgot_done";

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showErrorMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [error, setError] = useState("");

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: async (values) => {
      setError("");
      // 1. Try admin login first
      try {
        const loginResponse = await login(values).unwrap();
        if (loginResponse.status) {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          dispatch(setEnableAuth());
          navigate('/dashboard/list');
          return;
        }
      } catch (_) {
        // Admin login failed — fall through to portal login
      }

      // 2. Try portal (shop owner) login with same credentials
      setPortalLoading(true);
      try {
        const res = await fetch(`${API_URL}/restaurant-portal/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: values.username, password: values.password }),
        });
        const data = await res.json();
        if (data.success) {
          localStorage.setItem("portal_token", data.token);
          localStorage.setItem("portal_shop_id", String(data.shop_id));
          navigate(`/restaurant-portal/${data.shop_id}/dashboard`, { replace: true });
          return;
        }
      } catch (_) {}
      finally {
        setPortalLoading(false);
      }

      setError("Invalid username or password");
    },
  });

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true); setForgotMsg(""); setError("");
    try {
      const res = await fetch(`${API_URL}/restaurant-portal/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMsg(data.message || "If this email is registered, a reset link has been generated.");
      setMode("forgot_done");
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const formValid = useMemo(() => {
    return formik.values.username !== "" && formik.values.password !== "";
  }, [formik]);

  const anyLoading = isLoading || portalLoading;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", minHeight: "100vh" }}>
      {/* Left panel - branding */}
      <Box sx={{
        display: { xs: "none", md: "flex" }, width: "45%", bgcolor: BRAND_COLOR,
        flexDirection: "column", justifyContent: "center", alignItems: "center", p: 6,
        background: BRAND_GRADIENT,
      }}>
        <Box sx={{ mb: 4 }}>
          <img src={Logo} alt="Logo" style={{ width: 200, height: "auto", filter: "brightness(0) invert(1)" }} />
        </Box>
        <Typography sx={{ color: "#fff", fontSize: 32, fontWeight: 800, mb: 1.5, textAlign: "center", lineHeight: 1.2 }}>
          Welcome Back
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 15, textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>
          Manage your inventory, process orders, and grow your business — all in one place.
        </Typography>
        <Box sx={{ mt: 6, display: "flex", gap: 3 }}>
          {["POS System", "Inventory", "Orders", "Invoicing"].map(t => (
            <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }} />
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{t}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right panel - form */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#fff", p: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4, justifyContent: "center" }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: BRAND_COLOR, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <StorefrontIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography fontWeight={800} fontSize={20} color={BRAND_COLOR}>POS System</Typography>
          </Box>

          {mode === "login" && (
            <>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: BRAND_COLOR, mb: 0.5 }}>
                Sign In
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#999", mb: 3.5 }}>
                Enter your credentials to access your account
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

              <form onSubmit={formik.handleSubmit}>
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Username / Email</Typography>
                <TextField fullWidth size="small" placeholder="Enter your username or email"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  name="username"
                  required
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                  sx={{ mb: 2.5, ...inputSx }}
                />
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Password</Typography>
                <TextField fullWidth size="small" placeholder="Enter your password"
                  type={showPw ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  name="password"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment>,
                    endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPw(v => !v)}>{showPw ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}</IconButton></InputAdornment>,
                  }}
                  sx={{ mb: 1.5, ...inputSx }}
                />
                <Box sx={{ textAlign: "right", mb: 3 }}>
                  <Typography component="span" fontSize={13} color={BRAND_COLOR} fontWeight={600}
                    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => { setMode("forgot"); setError(""); setForgotMsg(""); setForgotEmail(""); }}>
                    Forgot password?
                  </Typography>
                </Box>
                <Button fullWidth type="submit" variant="contained" disabled={!formValid || anyLoading}
                  sx={{
                    borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 15,
                    bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#1e293b" },
                    textTransform: "none", boxShadow: "0 4px 14px rgba(26,26,46,0.35)",
                  }}>
                  {anyLoading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
                </Button>
              </form>
            </>
          )}

          {mode === "forgot" && (
            <>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: BRAND_COLOR, mb: 0.5 }}>
                Forgot Password
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#999", mb: 3.5 }}>
                Enter your portal email to receive a reset link
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

              <form onSubmit={handleForgot}>
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Email Address</Typography>
                <TextField fullWidth size="small" placeholder="Enter your email" type="email"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                  sx={{ mb: 3, ...inputSx }}
                />
                <Button fullWidth type="submit" variant="contained" disabled={forgotLoading}
                  sx={{
                    borderRadius: 2, py: 1.5, fontWeight: 700,
                    bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#1e293b" },
                    textTransform: "none",
                  }}>
                  {forgotLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Request Reset Link"}
                </Button>
                <Button fullWidth variant="text" startIcon={<ArrowBackIcon />}
                  onClick={() => { setMode("login"); setError(""); }}
                  sx={{ mt: 1.5, borderRadius: 2, color: "#999", textTransform: "none" }}>
                  Back to login
                </Button>
              </form>
            </>
          )}

          {mode === "forgot_done" && (
            <>
              <Box textAlign="center">
                <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#2e7d32", mb: 2 }} />
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: BRAND_COLOR, mb: 1 }}>
                  Check Your Email
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#999", mb: 1 }}>
                  {forgotMsg || "If this email is registered, a reset link has been generated."}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#999", mb: 3 }}>
                  Please contact your supplier to receive the reset link.
                </Typography>
                <Button fullWidth variant="outlined" startIcon={<ArrowBackIcon />}
                  onClick={() => { setMode("login"); setError(""); setForgotMsg(""); }}
                  sx={{ borderRadius: 2, color: BRAND_COLOR, borderColor: "#ccc", textTransform: "none" }}>
                  Back to login
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(Login);
